import { MapComponent, MapType } from '../map/map.component';
import { PrintButtonComponent } from '../shared/print-button.component';
import { RouteFcstInfoComponent } from './route-fcst-info.component';
import { RouteFcstLegendComponent } from './route-fcst-legend.component';

import { AuthService } from '../auth/auth.service';
import { AddressPopup } from '../map/map.service';
import { RadarDtg } from '../radar/radar.service';
import { LanguageService } from '../shared/services/language.service';
import {
  DetailInfo,
  RouteFcstService,
  RouteSituation,
} from './route-fcst.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, inject, model, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  ChangeContext,
  LabelType,
  NgxSliderModule,
  Options,
} from '@angular-slider/ngx-slider';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { TippyDirective } from '@ngneat/helipopper';
import moment from 'moment';
import { Moment } from 'moment';
import { sliderBaseOptions } from 'src/constants';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatButtonModule,
  NgxSliderModule,
  MatDividerModule,
  MatCardModule,
  MatTooltipModule,
  MatFormFieldModule,
  MatInputModule,
  ReactiveFormsModule,
  MatDatepickerModule,
  TranslateModule,
  MatSidenavModule,
  MatSlideToggleModule,
];
export const COMPONENTS = [
  FaIconComponent,
  PrintButtonComponent,
  RouteFcstInfoComponent,
  MapComponent,
  RouteFcstLegendComponent,
];
export const PIPES = [DatexPipe, IsNilPipe];
export const DIRECTIVES = [TippyDirective, WmIconDirective];

@Component({
  selector: 'app-route-fcst',
  templateUrl: './route-fcst.component.html',
  styleUrls: [
    './route-fcst.component.scss',
    './print-route-fcst.component.scss',
  ],
  providers: [
    RouteFcstService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS,
    },
  ],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class RouteFcstComponent implements OnInit {
  private breakpointObserver = inject(BreakpointObserver);
  private auth = inject(AuthService);
  public routeFcstService = inject(RouteFcstService);
  public languageService = inject(LanguageService);

  routeParameters = ['warn', 'rsc', 'rst', 'igp', 'igc'];
  selectedParameter = signal(this.routeParameters[0]);
  stations = [];
  roadZoomPoint;
  zoomPoint = { searchName: '', longitude: 0, latitude: 0 };
  productId: number;
  streetTypes = [];
  routeName = signal<string>(undefined);
  detailsData = signal<DetailInfo>(undefined);
  runTimeDtg = signal<Moment>(undefined);
  routeProduct: any;

  lastSelectedAddress: AddressPopup;

  sliderOptions = signal<Options>(sliderBaseOptions);
  sliderDate = model<number>();

  sliderArchiveOptions = signal<Options>(sliderBaseOptions);
  sliderArchiveDate = model<number>(undefined);

  opacityOptions: Options = {
    showTicksValues: true,
    stepsArray: [
      { value: 0, legend: 'hell' },
      { value: 0.1 },
      { value: 0.2 },
      { value: 0.3 },
      { value: 0.4 },
      { value: 0.5, legend: 'dunkel' },
    ],
    translate: (value: number, label: LabelType): string => {
      return value * 10 + '';
    },
  };

  settings = signal({
    backgroundOpacity: 0.2,
    designMapLeft: true,
  });
  // allow switch map to left
  allowDesignChange = signal(false);
  loadingImages = signal(true);
  dtgIndex = 0;
  dtgs: Array<RadarDtg> = [];

  archiveDtgIndex = signal<number>(0);
  archiveDtgs = signal<RadarDtg[]>([]);

  mapType: typeof MapType = MapType;

  serverError = signal(false);

  showArchive = signal(false);
  archiveDateControl = new FormControl<Moment>(moment().startOf('day'));
  minArchiveDate = moment()
    .set('year', moment().year() - 1)
    .set('month', 10)
    .set('date', 1);
  maxArchiveDate = moment();
  mapsArchiveCountMin = 0;
  mapsArchiveCountMax = 23;

  ngOnInit(): void {
    if (this.auth.isZuerichCustomer()) {
      this.routeFcstService.testMode.set(true);
    }
    this.readUserSettings();
    this.getRouteProduct();
  }

  // TODO: Read from User Profile
  readUserSettings(): void {
    const savedValue = localStorage.getItem('routeFcstSettings');
    if (savedValue) {
      const savedSettings = JSON.parse(savedValue);
      for (const key of Object.keys(savedSettings)) {
        // don't use saved map position
        if (key !== 'designMapLeft' || this.allowDesignChange()) {
          this.settings.update(settings => {
            return { ...settings, [key]: savedSettings[key] };
          });
        }
      }
    }
  }

  // TODO: Save to User Profile
  saveUserSettings(): void {
    localStorage.setItem('routeFcstSettings', JSON.stringify(this.settings()));
  }

  getRouteProduct(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const customerId = customerDocument['customerId'];
    for (const product of customerDocument['routeProducts']) {
      if (this.auth.isProductActive('routeProducts', product.id)) {
        this.routeProduct = product;
        if (this.routeFcstService.testMode()) {
          this.routeProduct.id = 3760;
        }
        this.productId = product.id;
        this.stations = product.stations;
        this.streetTypes = product.streetTypes;
        this.roadZoomPoint = product.zoomRegions.roads[0];
        this.routeName.set(this.roadZoomPoint['name']);
        this.zoomPoint = {
          searchName: this.routeName(),
          longitude: this.roadZoomPoint.mapCenter.longitude,
          latitude: this.roadZoomPoint.mapCenter.latitude,
        };
        this.detailsInfoBoxAndRuntimeCheck(customerId);
        break;
      }
    }
  }

  detailsInfoBoxAndRuntimeCheck(customerId): void {
    this.loadingImages.set(true);
    this.serverError.set(false);
    if (this.routeFcstService.testMode()) {
      const runTime = '2024-01-28T06:00:00Z';
      this.productId = 3760;
      customerId = 10018;
      this.runTimeDtg.set(moment(runTime)?.clone());
      this.routeFcstService
        .getRouteDetails(
          customerId,
          this.productId,
          runTime,
          this.roadZoomPoint
        )
        .subscribe(
          details => {
            this.detailsData.set(details);
            this.calculateDtgs();
            this.loadingImages.set(false);
          },
          err1 => {
            console.error('getRouteDetails', err1);
            this.serverError.set(true);
          }
        );
    } else {
      this.routeFcstService.getRuntimeCheck(this.productId, 101).subscribe(
        runTime => {
          this.runTimeDtg.set(moment(runTime)?.clone());
          // this.runTimeDtg = moment('2021-01-28T10:00:00Z');
          this.routeFcstService
            .getRouteDetails(
              customerId,
              this.productId,
              runTime,
              this.roadZoomPoint
            )
            .subscribe(
              details => {
                // detailsData = {...details};
                this.detailsData.set(details);
                this.calculateDtgs();
                this.loadingImages.set(false);
              },
              err1 => {
                console.error('getRuntimeCheck', err1);
                this.serverError.set(true);
              }
            );
        },
        err => console.error('getRuntimeCheck', err)
      );
    }
  }

  calculateDtgs(): void {
    const unixDates = [];
    let showFrame = true;

    if (this.showArchive()) {
      this.archiveDtgs.set([]);
      let startTime = this.archiveDateControl.value?.clone();
      for (
        let i = this.mapsArchiveCountMin;
        i <= this.mapsArchiveCountMax;
        i++
      ) {
        const dtg = startTime?.clone();
        const dtgUtc = moment(dtg).utc();
        const timestamp = dtg.unix();
        this.archiveDtgs.update(dtgs => {
          const updatedDtgs = [
            ...dtgs,
            {
              mapServer: dtgUtc.format('YYYYMMDDHHmm'),
              slider: timestamp,
              info: dtg.format('dd DD.MM.YYYY HH:mm'),
              visible: showFrame,
              isFcst: false,
              isArchive: false,
              issue: 0,
            },
          ];
          return updatedDtgs;
        });
        unixDates.push(timestamp);
        if (showFrame) {
          this.sliderArchiveDate.set(timestamp);
        }
        showFrame = false;
        startTime = startTime.add(1, 'hour');
      }
    } else {
      this.dtgs = [];
      for (const timestep of this.detailsData().dangerLevel) {
        // for (let timestep = 0; timestep <= 24; timestep++) {
        // const dtg = moment(this.runTimeDtg).add(timestep * 1, 'hours');
        const dtg = moment(timestep.dtg);
        const dtgUtc = moment(dtg).utc();
        const timestamp = dtg.unix();
        this.dtgs.push({
          mapServer: dtgUtc.format('YYYYMMDDHHmm'),
          slider: timestamp,
          info: dtg.format('dd DD.MM.YYYY HH:mm'),
          visible: showFrame,
          isFcst: false,
          isArchive: false,
          issue: 0,
        });
        unixDates.push(timestamp);
        if (showFrame) {
          this.sliderDate.set(timestamp);
        }
        showFrame = false;
      }
    }
    this.setSliderOptions(unixDates);
  }

  setSliderOptions(unixDates: number[]): void {
    if (this.showArchive()) {
      this.sliderArchiveOptions.set({
        showTicks: true,
        showTicksValues: true,
        ticksTooltip: (value: number): string => {
          return moment.unix(value).format('HH:mm');
        },
        stepsArray: unixDates.map((step: number) => {
          return {
            value: step,
            legend: '',
          };
        }),
        getTickColor: (step: number): string => {
          return '#399DE6';
        },
        translate: (value: number, label: LabelType): string => {
          const stepDtg = moment.unix(value);
          if (this.breakpointObserver.isMatched(['(max-width: 900px)'])) {
            return stepDtg.hours() % 3 === 0 ? stepDtg.format('HH') : '';
          } else {
            return stepDtg.hours() % 3 === 0 ? stepDtg.format('HH:mm') : '';
          }
        },
      });
    } else {
      const sliderOptions = {
        showTicks: true,
        showTicksValues: true,
        ticksTooltip: (value: number): string => {
          return moment.unix(value).format('HH:mm');
        },
        stepsArray: unixDates.map((step: number) => {
          const stepDtg = moment.unix(step);
          return {
            value: step,
            legend:
              stepDtg.isSameOrBefore(this.runTimeDtg()?.clone()) ||
              stepDtg.hours() === 0
                ? stepDtg.format('DD.MM.')
                : '',
          };
        }),
        getTickColor: (step: number): string => {
          return this.routeFcstService.colorMappingWarnDanger(
            this.getDangerLevel(step)
          );
        },
        translate: (value: number, label: LabelType): string => {
          const stepDtg = moment.unix(value);
          if (this.breakpointObserver.isMatched(['(max-width: 900px)'])) {
            return stepDtg.hours() % 3 === 0 ? stepDtg.format('HH') : '';
          } else {
            return stepDtg.hours() % 3 === 0 ? stepDtg.format('HH:mm') : '';
          }
        },
      };
      this.sliderOptions.set({ ...sliderOptions });
    }
  }

  getDangerLevel(timestamp: number): number {
    const dtg = moment.unix(timestamp).utc();
    if (this.routeFcstService.testMode()) {
      const hour = dtg.hours();
      const level = hour < 3 ? 3 : hour < 10 ? 2 : hour < 16 ? 1 : 0;
      return level;
    }
    for (const dangerLevel of this.detailsData().dangerLevel) {
      const dangerLevelDtg = moment(dangerLevel.dtg).utc();
      if (dtg.isSame(dangerLevelDtg)) {
        return dangerLevel.item;
      }
    }
    return 0;
  }

  setRouteTime(timestamp: number): void {
    if (this.showArchive()) {
      for (let i = 0; i < this.archiveDtgs().length; i++) {
        const currentDtgSlider = this.archiveDtgs()[i].slider;
        if (currentDtgSlider === timestamp) {
          this.setImageOpacity(i);
          this.archiveDtgIndex.set(i);
        }
      }
    } else {
      for (let i = 0; i < this.dtgs.length; i++) {
        if (this.dtgs[i].slider === timestamp) {
          this.setImageOpacity(i);
          this.dtgIndex = i;
        }
      }
    }
  }

  setImageOpacity(index: number): void {
    if (this.showArchive()) {
      this.archiveDtgs.update(dtgs => {
        return dtgs.map((dtg, i) => {
          return { ...dtg, visible: i === index };
        });
      });
    } else {
      for (let i = 0; i < this.dtgs.length; i++) {
        this.dtgs[i].visible = i === index;
      }
    }
  }

  onArchiveDateChange(event: MatDatepickerInputEvent<Moment>) {
    this.archiveDateControl.setValue(event.value);
    this.archiveDtgIndex.set(0);
    this.archiveDtgs.set([]);
    this.calculateDtgs();
    this.sliderArchiveDate.set(
      this.archiveDtgs()[this.archiveDtgIndex()].slider
    );
  }

  onSetShowArchive(event: MatSlideToggleChange) {
    this.showArchive.set(event.checked);
    this.calculateDtgs();
    this.sliderArchiveDate.set(
      this.archiveDtgs()[this.archiveDtgIndex()].slider
    );
  }

  onSliderChange(event: ChangeContext): void {
    this.setRouteTime(event.value);
  }

  onJumpToSituation(event: RouteSituation): void {
    if (this.showArchive()) {
      const currentDtg = this.archiveDtgs().find(
        dtg => dtg.slider === event.dtg.unix()
      );

      if (isNil(currentDtg)) return;

      this.setRouteTime(event.dtg.unix());
      this.sliderArchiveDate.set(
        this.archiveDtgs()[this.archiveDtgIndex()].slider
      );
    } else {
      this.setRouteTime(event.dtg.unix());
      this.sliderDate.set(this.dtgs[this.dtgIndex].slider);
    }
    this.selectedParameter.set(event.parameter);
  }

  onAddressSelect(addressInfo: AddressPopup): void {
    if (addressInfo && addressInfo.position) {
      this.lastSelectedAddress = addressInfo;
    }
  }

  onParameterSelect(parameter: string): void {
    this.selectedParameter.set(parameter);
  }

  onDesignChange(event: MatSlideToggleChange): void {
    this.settings.update(settings => {
      return { ...settings, designMapLeft: event.checked };
    });
    this.saveUserSettings();
  }

  onResetSettings(): void {
    const settings = {
      backgroundOpacity: 0.2,
      designMapLeft: true,
    };
    this.settings.set({ ...settings });

    this.saveUserSettings();
  }

  onBackgroundOpacityChange(event: ChangeContext) {
    this.settings.update(settings => {
      return { ...settings, backgroundOpacity: event.value };
    });
  }
}
